// components/insights-feed.jsx — Insights section (home page teaser + full
// index, category chips, pagination). Content comes from lib/insights.js at
// build time (baked into the static export), not a client-side fetch —
// every insight gets a real, crawlable /insights/<slug> URL. Publishing a
// new insight means editing data/insights.json and redeploying.

'use client';

import Link from 'next/link';
import { Photo, Headline } from './ui';

// Shared insight card — used on the home page, the Insights index, category
// archives and the "more insights" rail on a post's own page. External
// items (a linked PDF/document) open in a new tab; everything else links
// to its own /insights/<slug> page.
function InsightCard({ it }) {
  const inner = (
    <>
      <Photo aspect="4/3" tone={it.tone} src={it.image} caption={(it.title || '').slice(0, 32)} />
      <div style={{ marginTop: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, gap: 12 }}>
          <span style={{
            fontFamily: '"Source Sans 3", sans-serif', fontSize: 12, fontWeight: 700,
            letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--accent)',
          }}>{it.category}</span>
          <span style={{ fontSize: 13, color: 'var(--mid)', whiteSpace: 'nowrap' }}>{it.date}</span>
        </div>
        <Headline size="sm" as="h3" style={{ lineHeight: 1.25, maxWidth: '24ch' }}>{it.title}</Headline>
        {it.blurb && (
          <p style={{ margin: '12px 0 0', fontSize: 14, lineHeight: 1.55, color: 'var(--fg2)', maxWidth: '40ch' }}>
            {it.blurb}
          </p>
        )}
        {it.href && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 16,
            fontFamily: '"Source Sans 3", sans-serif', fontSize: 14, fontWeight: 600,
            color: 'var(--fg)',
          }}>
            <span style={{ borderBottom: '1px solid currentColor', paddingBottom: 2 }}>Read the document</span>
            <span style={{ color: 'var(--accent)' }}>→</span>
          </span>
        )}
      </div>
    </>
  );
  const baseStyle = { cursor: 'pointer', textDecoration: 'none', color: 'inherit', display: 'block' };
  if (it.href) {
    return <a href={it.href} target="_blank" rel="noreferrer" style={baseStyle}>{inner}</a>;
  }
  return <Link href={`/insights/${it.slug}`} style={baseStyle}>{inner}</Link>;
}

// Category filter chips. `active` is a category slug or null (= "All").
// Real <Link>s, not buttons — each chip is its own crawlable archive URL.
function CategoryChips({ categories, active }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
      <Link href="/insights" style={{
        padding: '8px 16px', fontSize: 14, fontWeight: 500, textDecoration: 'none',
        border: `1px solid ${!active ? 'var(--fg)' : 'var(--hair-s)'}`,
        background: !active ? 'var(--fg)' : 'transparent',
        color: !active ? 'var(--bg)' : 'var(--fg)',
      }}>All</Link>
      {categories.map((c) => (
        <Link key={c.slug} href={`/insights/category/${c.slug}`} style={{
          padding: '8px 16px', fontSize: 14, fontWeight: 500, textDecoration: 'none',
          border: `1px solid ${active === c.slug ? 'var(--fg)' : 'var(--hair-s)'}`,
          background: active === c.slug ? 'var(--fg)' : 'transparent',
          color: active === c.slug ? 'var(--bg)' : 'var(--fg)',
        }}>{c.name} <span style={{ opacity: 0.6 }}>({c.count})</span></Link>
      ))}
    </div>
  );
}

// Pagination controls. `basePath` is the un-paginated index the reader is
// browsing (/insights or /insights/category/<slug>) — page 1 has no /page/1
// suffix, matching the routing convention used by app/insights/page/[page]
// and app/insights/category/[slug]/page/[page].
function Pagination({ pagination, basePath }) {
  const { page, totalPages, hasPrev, hasNext } = pagination;
  if (totalPages <= 1) return null;
  const hrefFor = (n) => (n === 1 ? basePath : `${basePath}/page/${n}`);
  return (
    <nav aria-label="Insights pagination" style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      marginTop: 56, flexWrap: 'wrap',
    }}>
      {hasPrev ? (
        <Link href={hrefFor(page - 1)} style={pageLinkStyle(false)}>&larr; Newer</Link>
      ) : (
        <span style={pageLinkStyle(false, true)}>&larr; Newer</span>
      )}
      {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
        <Link key={n} href={hrefFor(n)} aria-current={n === page ? 'page' : undefined} style={pageLinkStyle(n === page)}>
          {n}
        </Link>
      ))}
      {hasNext ? (
        <Link href={hrefFor(page + 1)} style={pageLinkStyle(false)}>Older &rarr;</Link>
      ) : (
        <span style={pageLinkStyle(false, true)}>Older &rarr;</span>
      )}
    </nav>
  );
}

function pageLinkStyle(active, disabled = false) {
  return {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    minWidth: 36, height: 36, padding: '0 12px',
    fontSize: 14, fontWeight: 600, textDecoration: 'none',
    border: `1px solid ${active ? 'var(--fg)' : 'var(--hair-s)'}`,
    background: active ? 'var(--fg)' : 'transparent',
    color: disabled ? 'var(--ash)' : active ? 'var(--bg)' : 'var(--fg)',
    opacity: disabled ? 0.5 : 1,
  };
}

export { InsightCard, CategoryChips, Pagination };
